import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_BILDSCHIRMPRAESENTATION } from "../makros";

export const lektionReferentenansicht: Lesson = {
  id: "referentenansicht",
  title: "Die Referentenansicht",
  summary: "Zwei Bildschirme, zwei verschiedene Bilder – und alles Nötige im Blick.",
  durationMinutes: 8,
  learningGoals: [
    "Die Referentenansicht einrichten und lesen",
    "Zwischen Beamer und eigenem Bildschirm sicher unterscheiden",
    "Die wichtigsten Tastenkürzel im Vortrag beherrschen",
  ],
  steps: [
    {
      id: "einrichten",
      title: "Einrichten",
      explanation: [
        "Sobald ein zweiter Bildschirm angeschlossen ist, startet PowerPoint die " +
          "Präsentation automatisch in der Referentenansicht: Auf dem Beamer läuft die " +
          "Folie, auf dem eigenen Bildschirm erscheinen die aktuelle Folie, die nächste " +
          "Folie, die Sprechernotizen, eine Uhr und eine Werkzeugleiste.",
        "Vertauscht PowerPoint die Bildschirme – die Notizen landen auf dem Beamer –, " +
          "hilft in der Referentenansicht die Schaltfläche „Anzeigeeinstellungen“ → " +
          "„Referentenansicht und Bildschirmpräsentation austauschen“. Denselben Effekt " +
          "hat vorher auf der Registerkarte „Bildschirmpräsentation“ die Auswahl unter " +
          "„Monitor“.",
        "Wichtig ist die Windows-Anzeigeeinstellung „Erweitern“ statt „Duplizieren“. Bei " +
          "„Duplizieren“ zeigen beide Bildschirme dasselbe Bild, und die " +
          "Referentenansicht kann gar nicht funktionieren. Umschalten geht mit der " +
          "Tastenkombination Windows + P.",
        "Ohne zweiten Bildschirm lässt sich die Ansicht trotzdem ausprobieren: " +
          "Rechtsklick während der laufenden Präsentation → „Referentenansicht anzeigen“.",
      ],
      keyPoints: [
        "Windows + P → „Erweitern“, nicht „Duplizieren“.",
        "Bildschirmpräsentation → Monitor wählt den Beamer aus.",
        "Rechtsklick während der Präsentation → Referentenansicht anzeigen.",
      ],
      task: {
        instruction:
          "Stelle auf der Registerkarte „Bildschirmpräsentation“ sicher, dass " +
          "„Referentenansicht verwenden“ eingeschaltet ist.",
        hint: "Bildschirmpräsentation → Gruppe „Monitore“ → Häkchen „Referentenansicht verwenden“.",
        macro: MAKRO_BILDSCHIRMPRAESENTATION,
        check: () =>
          pruefeMitMakro(MAKRO_BILDSCHIRMPRAESENTATION, (bericht) =>
            fromCriteria(
              [criterion("„Referentenansicht verwenden“ ist eingeschaltet", bericht.janein("referentenansicht") === true)],
              {
                success: "Beim nächsten Vortrag mit zwei Bildschirmen siehst du Notizen, Uhr und nächste Folie.",
                failure: "Die Referentenansicht ist ausgeschaltet.",
              },
            ),
          ),
      },
    },
    {
      id: "waehrend-des-vortrags",
      title: "Während des Vortrags",
      explanation: [
        "Die Referentenansicht zeigt links die aktuelle Folie, rechts oben die nächste " +
          "und rechts unten die Notizen. Die Vorschau auf die nächste Folie ist der " +
          "unterschätzte Teil: Sie erlaubt es, den Übergang anzukündigen, statt " +
          "überrascht zu werden.",
        "Oben links läuft die Zeit. Ein Klick auf das Pausensymbol hält sie an, der " +
          "Pfeil daneben setzt sie zurück – hilfreich, wenn man vor dem eigentlichen " +
          "Beginn schon einmal durch die Folien geht.",
        "In der Werkzeugleiste unter der Folie stehen die wichtigsten Hilfen: der Stift " +
          "zum Zeichnen auf der Folie, der Laserpointer, die Foliensortierung zum " +
          "gezielten Springen, die Zoom-Lupe zum Vergrößern eines Ausschnitts und die " +
          "Schwarzblende.",
        "Die Schwarzblende ist das Werkzeug mit dem besten Verhältnis von Aufwand zu " +
          "Wirkung. Die Taste B schaltet den Beamer auf Schwarz, W auf Weiß, ein " +
          "erneuter Druck zurück. Wenn eine Diskussion entsteht, richtet sich die " +
          "Aufmerksamkeit damit sofort auf die Personen im Raum statt auf die Folie.",
      ],
      keyPoints: [
        "B blendet den Beamer schwarz, W weiß – nochmal drücken holt die Folie zurück.",
        "Foliennummer tippen + Enter springt gezielt zu einer Folie.",
        "Strg + L schaltet den Laserpointer ein, Strg + P den Stift.",
      ],
    },
    {
      id: "vorbereiten",
      title: "Vorher testen",
      explanation: [
        "Die drei Pannen, die im Ernstfall Zeit kosten, lassen sich vorher in fünf " +
          "Minuten ausschließen.",
        "Erstens die Auflösung: Ein Beamer mit 4:3-Format schneidet eine " +
          "Breitbildpräsentation ab oder zeigt sie mit Balken. Wenn bekannt ist, dass ein " +
          "älteres Gerät verwendet wird, lässt sich das Format vorher unter „Entwurf“ → " +
          "„Foliengröße“ umstellen – das verschiebt allerdings Objekte und will " +
          "nachkontrolliert werden.",
        "Zweitens Schriftarten: Läuft die Präsentation auf einem fremden Rechner, fehlt " +
          "dort möglicherweise die verwendete Schrift. Entweder Schriftarten einbetten " +
          "oder von vornherein nur verbreitete Schriften verwenden.",
        "Drittens Medien: Videos und Töne werden in der Datei eingebettet, wenn sie über " +
          "„Einfügen“ → „Video“ → „Dieses Gerät“ eingefügt wurden. Ein per Link " +
          "eingebundenes Video braucht dagegen die Originaldatei am selben Ort. Vor dem " +
          "Vortrag lohnt sich der Blick unter „Datei“ → „Informationen“ → " +
          "„Medienkompatibilität optimieren“, falls dort etwas angeboten wird.",
        "Und schließlich: einmal vollständig durchklicken, auf dem Gerät, das später " +
          "verwendet wird. Das findet mehr Probleme als jede Prüfliste.",
      ],
      keyPoints: [
        "Entwurf → Foliengröße stellt zwischen 16:9 und 4:3 um.",
        "Videos über „Dieses Gerät“ einfügen, damit sie eingebettet sind.",
        "Immer einmal auf dem Vortragsgerät durchklicken.",
      ],
    },
  ],
};
